import { useIsNarrow } from '@hooks'
import React from 'react'
import AccommodationsTile from './AccommodationsTile'
import { OverviewMode, resolveTileState, TripOverviewData, TripOverviewSection } from './core'
import FilesTile from './FilesTile'
import FlightsTile from './FlightsTile'
import IdeasTile from './IdeasTile'
import ItineraryTile from './ItineraryTile'
import MoneyTile from './MoneyTile'
import { BENTO_COLLAPSE_PX, orderedSections } from './sectionLayout'
import { BentoGrid } from './TripOverview.styles'

interface TripOverviewProps {
	data: TripOverviewData
	mode: OverviewMode
	/** Public-mode per-section share flags. Ignored in member mode. */
	shareFlags?: Partial<Record<TripOverviewSection, boolean>>
	/** Per-section query loading — a loading tile renders its own skeleton (no page spinner). */
	loading?: Partial<Record<TripOverviewSection, boolean>>
	/** Navigate to a section's own page. */
	onNavigate: (section: TripOverviewSection) => void
	/** Open the trip-dates modal (itinerary no-dates affordance). */
	onSetTripDates: () => void
}

/**
 * The trip overview bento grid. Each tile branches on its resolved state
 * (detail / empty / teaser) and skeletons while its own query loads. Render
 * order comes from `orderedSections` so the mobile reorder keeps visual and
 * keyboard tab order aligned.
 */
const TripOverview: React.FC<TripOverviewProps> = ({
	data,
	mode,
	shareFlags,
	loading,
	onNavigate,
	onSetTripDates,
}) => {
	const layout = useIsNarrow(BENTO_COLLAPSE_PX) ? 'mobile' : 'desktop'

	const stateFor = (section: TripOverviewSection, hasData: boolean) =>
		resolveTileState(mode, hasData, shareFlags?.[section])

	const tiles: Record<TripOverviewSection, React.ReactNode> = {
		itinerary: (
			<ItineraryTile
				section={data.itinerary}
				state={stateFor('itinerary', data.itinerary.eventCount > 0)}
				onNavigate={() => onNavigate('itinerary')}
				onSetDates={onSetTripDates}
				loading={loading?.itinerary}
			/>
		),
		ideas: (
			<IdeasTile
				section={data.ideas}
				state={stateFor('ideas', data.ideas.count > 0)}
				onNavigate={() => onNavigate('ideas')}
				loading={loading?.ideas}
			/>
		),
		accommodations: (
			<AccommodationsTile
				section={data.accommodations}
				state={stateFor('accommodations', data.accommodations.count > 0)}
				onNavigate={() => onNavigate('accommodations')}
				loading={loading?.accommodations}
			/>
		),
		flights: (
			<FlightsTile
				section={data.flights}
				state={stateFor('flights', data.flights.firstFlight != null)}
				onNavigate={() => onNavigate('flights')}
				loading={loading?.flights}
			/>
		),
		files: (
			<FilesTile
				section={data.files}
				state={stateFor('files', data.files.count > 0)}
				onNavigate={() => onNavigate('files')}
				loading={loading?.files}
			/>
		),
		money: (
			<MoneyTile
				section={data.money}
				state={stateFor('money', data.money.hasExpenses || data.money.payUpCount > 0)}
				onNavigate={() => onNavigate('money')}
				loading={loading?.money}
			/>
		),
	}

	return (
		<BentoGrid>
			{orderedSections(layout).map(section => (
				<React.Fragment key={section}>{tiles[section]}</React.Fragment>
			))}
		</BentoGrid>
	)
}

export default TripOverview
